import React, { useState } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import Select from 'react-select';
import { ModalButtonBuilder, ToastBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import { Trash2 } from 'react-feather';

/**
 * CompositeChainBuilder — Structured builder for mapping_options chain configuration.
 *
 * Provides a UI to define source_chain and destination_chain config,
 * then serializes to JSON and injects into the mapping options textarea.
 *
 * Output format:
 * {
 *   "source_chain": {
 *     "type": "association_table",
 *     "boards": [199, 200],
 *     "chain_labels": ["Type", "SubType", "Item"],
 *     "chain_levels": ["type", "subtype", "item"]
 *   },
 *   "destination_chain": {
 *     "chain_labels": ["Category", "Subcategory", "Item"],
 *     "chain_resources": [1045, 1046, 1047]
 *   }
 * }
 */

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    multiValue: (base) => ({ ...base, fontSize: 13, padding: '2px 4px' }),
    multiValueLabel: (base) => ({ ...base, fontSize: 13 }),
};

const SOURCE_CHAIN_TYPES = [
    { value: 'none', label: 'None (Destination Only)' },
    { value: 'association_table', label: 'Association Table' },
];

// TSI association tables have a fixed 3-level chain: keys are locked and ordered, only labels are editable.
const TSI_LEVELS = ['type', 'subtype', 'item'];
const TSI_DEFAULT_LABELS = { type: 'Type', subtype: 'SubType', item: 'Item' };

// Zip the stored positional arrays back into step rows for editing. Keys are locked to the fixed TSI sequence by
// position (type, subtype, item); only labels carry over. Clamp to 3.
const zipSteps = (labels) => {
    const n = Math.min(labels?.length || 0, TSI_LEVELS.length);
    const steps = [];
    for (let i = 0; i < n; i++) {
        const level = TSI_LEVELS[i];
        steps.push({ label: labels?.[i] || TSI_DEFAULT_LABELS[level], level });
    }
    return steps;
};

const CompositeChainBuilder = ({ currentValue, onApply, readOnly, jobResources, destinationConnectionId }) => {

    // Source chain state
    const [sourceType, setSourceType] = useState(null);
    const [sourceBoards, setSourceBoards] = useState('');
    // chainSteps: ordered [{ label, level }] parent→child. Serializes to the positional chain_labels/chain_levels arrays.
    const [chainSteps, setChainSteps] = useState([]);
    // leaf_identity: per-level map (level -> array of level names whose values form that leaf's key). Empty = modal default.
    const [leafIdentity, setLeafIdentity] = useState({});

    // Destination chain state: ordered [{ resourceId, label }] parent→child. Serializes to chain_resources/chain_labels.
    const [destSteps, setDestSteps] = useState([]);

    const destinationResources = jobResources?.[destinationConnectionId]?.selected_resources || [];
    const destResourceOptions = buildOptionsFromArray(
        destinationResources.map(r => ({ ...r, resource_id: String(r.resource_id) })),
        { label: 'name', value: 'resource_id' }
    );

    const loadFromCurrent = () => {
        const raw = typeof currentValue === 'function' ? currentValue() : currentValue;
        const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;

        if (!parsed || typeof parsed !== 'object') {
            resetFields();
            return;
        }

        // Load source chain
        if (parsed.source_chain) {
            const sc = parsed.source_chain;
            setSourceType(sc.type || null);
            setSourceBoards(Array.isArray(sc.boards) ? sc.boards.join(', ') : '');
            setChainSteps(zipSteps(sc.chain_labels));
            setLeafIdentity(sc.leaf_identity && typeof sc.leaf_identity === 'object' && !Array.isArray(sc.leaf_identity) ? sc.leaf_identity : {});
        } else {
            setSourceType('none');
            setSourceBoards('');
            setChainSteps([]);
            setLeafIdentity({});
        }

        // Load destination chain
        if (parsed.destination_chain) {
            const dc = parsed.destination_chain;
            const ids = Array.isArray(dc.chain_resources) ? dc.chain_resources.map(String) : [];
            const labels = Array.isArray(dc.chain_labels) ? dc.chain_labels : [];
            setDestSteps(ids.map((rid, i) => ({ resourceId: rid, label: labels[i] || '' })));
        } else {
            setDestSteps([]);
        }
    };

    const resetFields = () => {
        setSourceType('none');
        setSourceBoards('');
        setChainSteps([]);
        setLeafIdentity({});
        setDestSteps([]);
    };

    const handleApply = () => {
        const result = {};

        // Build source_chain
        if (sourceType && sourceType !== 'none') {
            const sourceChain = { type: sourceType };

            if (sourceType === 'association_table') {
                if (sourceBoards.trim()) {
                    sourceChain.boards = sourceBoards.split(',').map(b => parseInt(b.trim())).filter(n => !isNaN(n));
                }
                const applySteps = chainSteps.filter(s => s.label.trim());
                if (applySteps.length) {
                    sourceChain.chain_labels = applySteps.map(s => s.label.trim());
                    sourceChain.chain_levels = applySteps.map(s => s.level);
                }
                const leafOut = {};
                for (const [lvl, fields] of Object.entries(leafIdentity)) {
                    if (Array.isArray(fields) && fields.length) leafOut[lvl] = fields;
                }
                if (Object.keys(leafOut).length) sourceChain.leaf_identity = leafOut;
            }

            result.source_chain = sourceChain;
        }

        // Build destination_chain
        const destRows = destSteps.filter(s => s.resourceId);
        if (destRows.length > 0) {
            result.destination_chain = {
                chain_resources: destRows.map(s => parseInt(s.resourceId)).filter(n => !isNaN(n)),
                chain_labels: destRows.map(s => (s.label || '').trim() || labelForResource(s.resourceId)),
            };
        }

        if (Object.keys(result).length === 0) {
            onApply?.('');
        } else {
            onApply?.(JSON.stringify(result, null, 2));
        }

        ToastBuilder('info', "Don't forget to Submit to save", {}, 3000);
    };

    // Chain-step mutators. TSI is a fixed type→subtype→item chain: append the next level only, remove only the last,
    // no reorder, keys locked. Only the label is editable.
    const canAddStep = chainSteps.length < TSI_LEVELS.length;
    const addStep = () => setChainSteps(prev => {
        if (prev.length >= TSI_LEVELS.length) return prev;
        const level = TSI_LEVELS[prev.length];
        return [...prev, { label: TSI_DEFAULT_LABELS[level], level }];
    });
    const removeStep = (idx) => setChainSteps(prev => (idx === prev.length - 1 ? prev.slice(0, -1) : prev));
    const updateStepLabel = (idx, label) => setChainSteps(prev => prev.map((s, i) => i === idx ? { ...s, label } : s));

    // Levels/labels for the leaf-identity matrix, derived from the chain steps.
    const steps = chainSteps.filter(s => s.level);
    const leafLevels = steps.map(s => s.level);
    const leafLabels = steps.map((s, i) => s.label.trim() || TSI_DEFAULT_LABELS[s.level] || leafLevels[i]);
    const labelFor = (lvl, i) => leafLabels[i] || lvl;

    // Default identity when unset for a level: its own name, plus (for levels below the first) the immediate parent.
    const defaultIdentityFor = (lvl, i) => (i <= 0 ? [lvl] : [lvl, leafLevels[i - 1]]);
    const identityFor = (lvl, i) => (Array.isArray(leafIdentity[lvl]) ? leafIdentity[lvl] : defaultIdentityFor(lvl, i));

    const toggleIdentity = (lvl, i, field) => {
        if (readOnly) return;
        setLeafIdentity(prev => {
            const current = Array.isArray(prev[lvl]) ? prev[lvl] : defaultIdentityFor(lvl, i);
            const next = current.includes(field) ? current.filter(f => f !== field) : [...current, field];
            // Keep selection ordered by chain position for readable output.
            next.sort((a, b) => leafLevels.indexOf(a) - leafLevels.indexOf(b));
            return { ...prev, [lvl]: next };
        });
    };

    // Destination step helpers. Order is user-defined (parent → child), so free add/reorder/remove, each row a resource.
    const labelForResource = (rid) => destResourceOptions.find(o => o.value === String(rid))?.label || '';
    const addDestStep = () => setDestSteps(prev => [...prev, { resourceId: '', label: '' }]);
    const removeDestStep = (idx) => setDestSteps(prev => prev.filter((_, i) => i !== idx));
    const moveDestStep = (idx, dir) => setDestSteps(prev => {
        const j = idx + dir;
        if (j < 0 || j >= prev.length) return prev;
        const next = [...prev];
        [next[idx], next[j]] = [next[j], next[idx]];
        return next;
    });
    const updateDestResource = (idx, resourceId) => setDestSteps(prev => prev.map((s, i) => {
        if (i !== idx) return s;
        // Auto-fill label from the picked resource unless the user already typed a custom one.
        const autoLabel = !s.label || s.label === labelForResource(s.resourceId);
        return { resourceId, label: autoLabel ? labelForResource(resourceId) : s.label };
    }));
    const updateDestLabel = (idx, label) => setDestSteps(prev => prev.map((s, i) => i === idx ? { ...s, label } : s));

    return (
        <ModalButtonBuilder
            title='Chain Builder'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 110 }}
            modalTitle='Composite Chain Configuration'
            formId='composite-chain-builder-form'
            style={{ width: 680, maxWidth: '95vw' }}
            onSubmit={readOnly ? false : 'Apply'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadFromCurrent}
            body={
                <Form id='composite-chain-builder-form' className='input-sm' onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleApply(); }}>

                    {/* Source Chain */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px' }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 10 }}>Source Chain</span>

                        <Row className='mb-2'>
                            <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Type</label></Col>
                            <Col md={8}>
                                <div className='react-select-sm'>
                                    <Select
                                        className='react-select-container'
                                        classNamePrefix='react-select'
                                        options={SOURCE_CHAIN_TYPES}
                                        value={SOURCE_CHAIN_TYPES.find(o => o.value === sourceType) || null}
                                        onChange={(opt) => setSourceType(opt?.value || 'none')}
                                        isDisabled={readOnly}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition='fixed'
                                        placeholder='Select type...'
                                    />
                                </div>
                            </Col>
                        </Row>

                        {sourceType === 'association_table' && (
                            <>
                                <Row className='mb-2'>
                                    <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Boards</label></Col>
                                    <Col md={8}>
                                        <Input
                                            bsSize='sm'
                                            type='text'
                                            value={sourceBoards}
                                            onChange={(e) => setSourceBoards(e.target.value)}
                                            readOnly={readOnly}
                                            placeholder='199, 200, 332'
                                            style={{ fontSize: 12 }}
                                        />
                                        <small className='text-muted' style={{ fontSize: 10 }}>Comma-separated board IDs</small>
                                    </Col>
                                </Row>
                                <Row className='mb-2'>
                                    <Col md={12}>
                                        <div className='d-flex align-items-center justify-content-between mb-1'>
                                            <label style={{ fontSize: 12, marginBottom: 0, fontWeight: 600 }}>Chain Steps</label>
                                            {!readOnly && canAddStep && (
                                                <Button type='button' color='primary' size='sm' style={{ fontSize: 11 }} onClick={addStep}>
                                                    + Add {TSI_DEFAULT_LABELS[TSI_LEVELS[chainSteps.length]]}
                                                </Button>
                                            )}
                                        </div>
                                        {chainSteps.length === 0 ? (
                                            <small className='text-muted' style={{ fontSize: 11 }}>Fixed TSI chain. Add Type, then SubType, then Item. Only the display label is editable.</small>
                                        ) : (
                                            <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                                                <div className='d-flex align-items-center px-2 py-1 text-muted' style={{ fontSize: 10, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                                    <span style={{ width: 80 }}>Level</span>
                                                    <span style={{ flex: 1 }}>Display label</span>
                                                    <span style={{ width: 24 }} />
                                                </div>
                                                {chainSteps.map((step, idx) => (
                                                    <div key={step.level} className='d-flex align-items-center px-2 py-1' style={{ borderBottom: idx < chainSteps.length - 1 ? '1px solid #f1f3f5' : 'none' }}>
                                                        <span style={{ width: 80 }}>
                                                            <span className='badge bg-light text-muted' style={{ fontSize: 11, fontFamily: 'monospace', border: '1px solid #dee2e6' }}>{step.level}</span>
                                                        </span>
                                                        <div style={{ flex: 1, paddingRight: 6 }}>
                                                            <Input bsSize='sm' type='text' value={step.label} readOnly={readOnly}
                                                                onChange={(e) => updateStepLabel(idx, e.target.value)}
                                                                placeholder={TSI_DEFAULT_LABELS[step.level]} style={{ fontSize: 12 }} />
                                                        </div>
                                                        {/* Only the last step can be removed, to keep the chain contiguous. */}
                                                        <span style={{ width: 24 }} className='d-flex justify-content-center'>
                                                            {!readOnly && idx === chainSteps.length - 1 && (
                                                                <Button type='button' color='link' size='sm' className='p-0 txt-danger' onClick={() => removeStep(idx)} title='Remove last step'>
                                                                    <Trash2 size={15} />
                                                                </Button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <small className='text-muted d-block mt-1' style={{ fontSize: 10 }}>The chain is fixed to Type → SubType → Item. Rename the label if your data uses different wording; the internal key stays the same.</small>
                                    </Col>
                                </Row>
                                <Row className='mb-2'>
                                    <Col md={12}>
                                        <label style={{ fontSize: 12, marginBottom: 2, fontWeight: 600 }}>Leaf Identity</label>
                                        <div className='text-muted mb-2' style={{ fontSize: 11 }}>
                                            Controls how Map-by-Leaf groups rows into one selectable leaf. For each level, pick which other levels must also match. The level itself always counts; untick a parent to collapse that leaf across it.
                                        </div>
                                        {leafLevels.length === 0 ? (
                                            <small className='text-muted' style={{ fontSize: 11 }}>Set Chain Levels above to configure this.</small>
                                        ) : (
                                            <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                                                {leafLevels.map((lvl, i) => {
                                                    const sel = identityFor(lvl, i);
                                                    const parents = leafLevels.slice(0, i); // only ancestors — a leaf can't be keyed by its descendants
                                                    return (
                                                        <div key={lvl} className='d-flex align-items-center px-3 py-2' style={{ borderBottom: i < leafLevels.length - 1 ? '1px solid #f1f3f5' : 'none', background: i % 2 ? '#fcfcfd' : '#fff' }}>
                                                            <span style={{ fontSize: 12 }}>
                                                                One <strong>{labelFor(lvl, i)}</strong> leaf per unique
                                                            </span>
                                                            <span className='badge ms-2' style={{ background: 'var(--theme-default)', color: '#fff', fontSize: 11 }} title='The level itself always identifies the leaf'>
                                                                {labelFor(lvl, i)}
                                                            </span>
                                                            {parents.length > 0 && <span className='text-muted mx-2' style={{ fontSize: 11 }}>combined with:</span>}
                                                            {[...parents].reverse().map((f) => {
                                                                const fi = leafLevels.indexOf(f);
                                                                const on = sel.includes(f);
                                                                return (
                                                                    <button
                                                                        key={f}
                                                                        type='button'
                                                                        disabled={readOnly}
                                                                        onClick={() => toggleIdentity(lvl, i, f)}
                                                                        className={`badge me-2 ${on ? 'bg-info text-white' : 'bg-light text-muted'}`}
                                                                        style={{ border: '1px solid #dee2e6', fontSize: 11, cursor: readOnly ? 'default' : 'pointer', opacity: on ? 1 : 0.7 }}
                                                                        title={on ? `Separate leaves per ${labelFor(f, fi)}` : `Collapse across ${labelFor(f, fi)}`}
                                                                    >
                                                                        {on ? '✓ ' : '+ '}{labelFor(f, fi)}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <small className='text-muted d-block mt-1' style={{ fontSize: 10 }}>
                                            Default: each level is its own value plus its immediate parent — e.g. an Item is one leaf per (Item + SubType), and a SubType collapses across its parent Types.
                                        </small>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>

                    {/* Destination Chain */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <div className='d-flex align-items-center justify-content-between mb-2'>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>Destination Chain</span>
                            {!readOnly && <Button type='button' color='primary' size='sm' style={{ fontSize: 11 }} onClick={addDestStep}>+ Add Level</Button>}
                        </div>

                        {destSteps.length === 0 ? (
                            <small className='text-muted' style={{ fontSize: 11 }}>Add destination levels top-down (parent → child), e.g. Category, Subcategory, Item.</small>
                        ) : (
                            <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                                <div className='d-flex align-items-center px-2 py-1 text-muted' style={{ fontSize: 10, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                    <span style={{ width: 24 }} />
                                    <span style={{ flex: 1.4 }}>Resource</span>
                                    <span style={{ flex: 1 }}>Display label</span>
                                    <span style={{ width: 70 }} />
                                </div>
                                {destSteps.map((step, idx) => (
                                    <div key={idx} className='d-flex align-items-center px-2 py-1' style={{ borderBottom: idx < destSteps.length - 1 ? '1px solid #f1f3f5' : 'none' }}>
                                        <span className='text-muted text-center' style={{ width: 24, fontSize: 11 }}>{idx + 1}</span>
                                        <div style={{ flex: 1.4, paddingRight: 6 }}>
                                            <div className='react-select-sm'>
                                                <Select
                                                    className='react-select-container'
                                                    classNamePrefix='react-select'
                                                    options={destResourceOptions.filter(o => o.value === String(step.resourceId) || !destSteps.some((s, j) => j !== idx && String(s.resourceId) === o.value))}
                                                    value={destResourceOptions.find(o => o.value === String(step.resourceId)) || null}
                                                    onChange={(opt) => updateDestResource(idx, opt?.value || '')}
                                                    isDisabled={readOnly}
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    menuPosition='fixed'
                                                    placeholder='Pick resource...'
                                                />
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, paddingRight: 6 }}>
                                            <Input bsSize='sm' type='text' value={step.label} readOnly={readOnly}
                                                onChange={(e) => updateDestLabel(idx, e.target.value)}
                                                placeholder={labelForResource(step.resourceId) || 'Label'} style={{ fontSize: 12 }} />
                                        </div>
                                        {!readOnly && (
                                            <span style={{ width: 70 }} className='d-flex justify-content-end align-items-center'>
                                                <Button type='button' color='link' size='sm' className='p-0 me-2 text-muted' disabled={idx === 0} onClick={() => moveDestStep(idx, -1)} title='Move up'>↑</Button>
                                                <Button type='button' color='link' size='sm' className='p-0 me-2 text-muted' disabled={idx === destSteps.length - 1} onClick={() => moveDestStep(idx, 1)} title='Move down'>↓</Button>
                                                <Button type='button' color='link' size='sm' className='p-0 txt-danger' onClick={() => removeDestStep(idx)} title='Remove'><Trash2 size={15} /></Button>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <small className='text-muted d-block mt-1' style={{ fontSize: 10 }}>Order is parent → child. The label defaults to the resource name; edit it only if you want different wording in the UI.</small>
                    </div>

                </Form>
            }
        />
    );
};

export default CompositeChainBuilder;
